import type { ReactNode } from 'react'
import { Box, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { BATIKAL_CGU_URL } from '@batical/common'

const pSx = { color: 'text.secondary', lineHeight: 1.7, mb: 1.5 } as const
const h2Sx = { fontWeight: 700, fontSize: '1.05rem', mt: 3, mb: 1.5, color: 'text.primary' } as const

function P({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" sx={pSx}>
      {children}
    </Typography>
  )
}

function H2({ children }: { children: ReactNode }) {
  return (
    <Typography component="h2" variant="h6" sx={h2Sx}>
      {children}
    </Typography>
  )
}

function Ul({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5, color: 'text.secondary' }}>
      {items.map(item => (
        <Typography key={item} component="li" variant="body2" sx={{ mb: 0.75, lineHeight: 1.65 }}>
          {item}
        </Typography>
      ))}
    </Box>
  )
}

export function PrivacyPolicyContent() {
  return (
    <>
      <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}>
        Version 1.0 — Dernière mise à jour : 16 mai 2026
      </Typography>

      <P>
        La présente politique décrit comment BatiKal (l&apos;« Éditeur ») traite les données personnelles dans le cadre
        de l&apos;application et du site web associés.
      </P>

      <H2>1. Responsable du traitement</H2>
      <P>Responsable du traitement : BatiKal — [Raison sociale et adresse postale à compléter].</P>
      <P>
        Contact pour toute question relative à vos données personnelles :{' '}
        <Link href="mailto:contact@batikal-app.com">contact@batikal-app.com</Link>.
      </P>
      <P>
        Aucun délégué à la protection des données (DPO) n&apos;a été désigné, cette désignation n&apos;étant pas
        obligatoire au sens de l&apos;art. 37 du RGPD au regard de l&apos;activité actuelle. Le responsable du
        traitement traite directement les demandes des personnes concernées.
      </P>

      <H2>2. Données traitées</H2>
      <P>Selon votre utilisation, sont susceptibles d&apos;être traitées les catégories suivantes :</P>
      <Ul
        items={[
          "Données de compte : adresse e-mail, identifiant, mot de passe (haché), date de création et de dernière connexion.",
          "Données de profil : informations renseignées librement (nom d’affichage, préférences).",
          "Données de calcul et sauvegardes : projets, calculs, paramètres et exports que vous choisissez d’enregistrer.",
          "Données techniques : adresse IP, identifiant d’appareil, version de l’application et du système d’exploitation, journaux d’événements, données de diagnostic et de crash.",
          "Données d’abonnement et de facturation (le cas échéant) : identifiants de transaction, montants, statut de l’abonnement. Les données bancaires complètes ne transitent pas par l’Éditeur et sont traitées directement par le prestataire de paiement.",
          "Preuve d’acceptation des CGU : version acceptée, date et horodatage."
        ]}
      />

      <H2>3. Finalités</H2>
      <P>Les données sont traitées pour les finalités suivantes :</P>
      <Ul
        items={[
          "Création et gestion du compte utilisateur ; fourniture du service de calcul et de sauvegarde.",
          "Support utilisateur et réponse aux demandes.",
          "Facturation et gestion des abonnements (le cas échéant).",
          "Sécurité de l’Application, prévention de la fraude et des abus.",
          "Amélioration du service et statistiques d’usage agrégées.",
          "Respect des obligations légales, comptables et fiscales ; réponse aux réquisitions des autorités compétentes."
        ]}
      />

      <H2>4. Bases légales</H2>
      <Ul
        items={[
          "Exécution du contrat (CGU) : création et gestion du compte, fourniture du service, sauvegardes, support, facturation — art. 6.1.b RGPD.",
          "Intérêt légitime de l’Éditeur : sécurité, prévention de la fraude, amélioration du service, statistiques agrégées — art. 6.1.f RGPD.",
          "Consentement : cookies et traceurs non strictement nécessaires, communications marketing le cas échéant — art. 6.1.a RGPD.",
          "Obligation légale : conservation comptable et fiscale, réponses aux réquisitions légales — art. 6.1.c RGPD."
        ]}
      />

      <H2>5. Destinataires et sous-traitants</H2>
      <P>
        Vos données peuvent être communiquées aux sous-traitants suivants, agissant sur instruction documentée de
        l&apos;Éditeur :
      </P>
      <Ul
        items={[
          'Hébergement et infrastructure : Google LLC / Firebase — hébergement, base de données, authentification, stockage, fonctions serveur, analytics technique.',
          'Paiement (le cas échéant) : Stripe Payments Europe Ltd. — traitement des paiements en cas de souscription payante.',
          'Distribution mobile : Apple Inc. (App Store) et Google LLC (Google Play) pour la distribution de l’application.'
        ]}
      />
      <P>
        Transferts hors Union européenne : certains sous-traitants (notamment Google LLC et, le cas échéant, Stripe Inc.)
        peuvent traiter des données aux États-Unis. Ces transferts sont encadrés par : (i) l&apos;EU-US Data Privacy
        Framework lorsque le sous-traitant y est certifié, et/ou (ii) les Clauses Contractuelles Types (CCT) adoptées par
        la Commission européenne (décision d&apos;exécution 2021/914 du 4 juin 2021), et/ou (iii) toute autre garantie
        appropriée au sens des art. 46 et suivants du RGPD. Une copie des garanties peut être obtenue sur demande à{' '}
        <Link href="mailto:contact@batikal-app.com">contact@batikal-app.com</Link>.
      </P>

      <H2>6. Durées de conservation</H2>
      <Ul
        items={[
          "Données de compte (e-mail, profil) : durée du compte, puis suppression dans les 30 jours suivant la suppression du compte par l’Utilisateur.",
          "Compte inactif : suppression automatique après 3 ans d’inactivité, conformément aux recommandations de la CNIL. Un email d’avertissement est envoyé avant suppression définitive.",
          "Données de calcul et sauvegardes : durée du compte, sauf suppression manuelle anticipée par l’Utilisateur.",
          "Logs techniques et journaux de connexion : 12 mois maximum.",
          "Données de facturation (le cas échéant) : 10 ans à compter de l’émission, en application de l’art. L123-22 du Code de commerce.",
          "Preuve d’acceptation des CGU (version, date, horodatage) : durée du compte, prolongée du délai de prescription civile applicable (5 ans, art. 2224 C. civ.)."
        ]}
      />

      <H2>7. Vos droits</H2>
      <P>
        Vous disposez des droits suivants sur vos données personnelles (art. 15 à 22 RGPD) : accès, rectification,
        effacement (« droit à l&apos;oubli »), opposition, limitation du traitement, portabilité, retrait du
        consentement à tout moment lorsque le traitement est fondé sur celui-ci, ainsi que le droit de définir des
        directives post-mortem (art. 85 de la loi Informatique et Libertés).
      </P>
      <P>
        Pour exercer ces droits, écrivez à{' '}
        <Link href="mailto:contact@batikal-app.com">contact@batikal-app.com</Link>. Une réponse vous sera apportée
        dans un délai d&apos;un (1) mois à compter de la réception de la demande ; ce délai peut être prolongé de
        deux (2) mois en cas de complexité ou de multiplicité des demandes, auquel cas vous en serez informé (art. 12
        RGPD).
      </P>
      <P>
        Vous pouvez à tout moment introduire une réclamation auprès de la CNIL :{' '}
        <Link href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
          www.cnil.fr
        </Link>
        .
      </P>

      <H2>8. Cookies et traceurs</H2>
      <P>
        L&apos;application web peut déposer des cookies strictement nécessaires au fonctionnement du service
        (session, authentification, préférences). Aucun cookie publicitaire ni traceur tiers à finalité marketing
        n&apos;est utilisé sans votre consentement. Lorsque des outils de mesure d&apos;audience ou d&apos;analyse
        (par exemple Firebase Analytics ou Crashlytics) sont activés, votre consentement préalable est recueilli via
        une bannière dédiée, conformément à la recommandation de la CNIL du 17 septembre 2020.
      </P>

      <H2>9. Décisions automatisées</H2>
      <P>
        L&apos;Application réalise des calculs automatisés à votre demande, à des fins pédagogiques, de simulation
        et d&apos;information. Ces calculs ne constituent pas une décision automatisée produisant des effets
        juridiques ou vous affectant de manière significative au sens de l&apos;art. 22 du RGPD : les Résultats
        sont informatifs et non opposables (voir CGU, art. 3 et 5).
      </P>

      <H2>10. Mineurs</H2>
      <P>
        L&apos;Application est destinée à un public majeur ou à des mineurs de plus de 15 ans, âge du consentement
        numérique en France (art. 7-1 de la loi Informatique et Libertés). Pour les mineurs de moins de 15 ans,
        l&apos;utilisation du service et le traitement des données personnelles associées requièrent le consentement
        conjoint du titulaire de l&apos;autorité parentale.
      </P>

      <H2>11. Sécurité</H2>
      <P>
        Mesures techniques et organisationnelles appropriées (chiffrement en transit, contrôle d&apos;accès,
        sauvegardes) ; aucune transmission sur Internet n&apos;est toutefois exempte de risque. En cas de violation
        de données susceptible d&apos;engendrer un risque élevé pour vos droits et libertés, vous serez informé dans
        les conditions de l&apos;art. 34 du RGPD.
      </P>

      <H2>12. Modifications de la présente politique</H2>
      <P>
        L&apos;Éditeur peut modifier la présente politique de confidentialité à tout moment. La version applicable
        est celle accessible sur la présente page à la date de la consultation. Les modifications substantielles
        vous seront notifiées par email ou via l&apos;Application.
      </P>

      <H2>13. Conditions d&apos;utilisation</H2>
      <P>
        Voir les{' '}
        <Link href={BATIKAL_CGU_URL} target="_blank" rel="noopener noreferrer">
          Conditions générales d&apos;utilisation
        </Link>
        .
      </P>

      <Typography variant="body2" sx={{ ...pSx, mt: 3 }}>
        <Link component={RouterLink} to="/login" underline="hover">
          Retour à la connexion
        </Link>
      </Typography>
    </>
  )
}
